import React, { useContext, useEffect, useState, useMemo, useCallback } from "react";
import { TranslatorContext } from "../../../context/Translator";
import { SidebarContext } from "../../../context/Sidebar";
import { Row, Col, Card, Modal, Form, Spinner } from "react-bootstrap";
import PageLayout from "../../../layouts/PageLayout";
import { LabelTextareaComponent } from "../../../components/fields";
import { ButtonComponent } from "../../../components/elements";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import Swal from "sweetalert2";
import { FaPlus } from "react-icons/fa";

const getTodayDate = () => new Date().toISOString().split("T")[0];

const showToast = (icon, title) => {
  Swal.fire({ toast: true, position: "top-end", icon, title, showConfirmButton: false, timer: 2000, timerProgressBar: true });
};

const emptyProductRow = { product_id: "", quantity: "", extra_quantity: "", purchase_price: "", sale_price: "", online_price: "" };

export default function PurchaseCreatePage() {
  const { t } = useContext(TranslatorContext);
  const { setSidebar } = useContext(SidebarContext);
  const navigate = useNavigate();
  const AuthToken = localStorage.getItem("authToken");

  const [loading, setLoading] = useState(true);
  const [vendors, setVendors] = useState([]);
  const [products, setProducts] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [categories, setCategories] = useState([]);
  const [errors, setErrors] = useState({});
  const [forceUpdate, setForceUpdate] = useState(0);

  const [currentProductRowIndex, setCurrentProductRowIndex] = useState(null);
  const [showVendorModal, setShowVendorModal] = useState(false);
  const [vendorForm, setVendorForm] = useState({ name: "", email: "", phone: "", address: "", img: null });
  const [vendorErrors, setVendorErrors] = useState({});
  const [vendorLoading, setVendorLoading] = useState(false);

  const [showProductModal, setShowProductModal] = useState(false);
  const [productForm, setProductForm] = useState({ name: "", cat_id: "", vendor_id: "" });
  const [productErrors, setProductErrors] = useState({});
  const [productLoading, setProductLoading] = useState(false);

  const [formData, setFormData] = useState({
    vendor_id: "",
    purchase_date: getTodayDate(),
    img: null,
    notes: "",
    tax: "",
    products: [{ ...emptyProductRow }],
    payments: [{ payment_method_id: "", amount: "", payment_date: getTodayDate() }],
  });

  const selectedVendor = useMemo(() => vendors.find((v) => v.id == formData.vendor_id) || null, [vendors, formData.vendor_id]);
  const selectedVendorBalance = selectedVendor?.balance || 0;
  const selectedVendorName = selectedVendor ? selectedVendor.company_name || selectedVendor.name : "";

  const grandTotal = useMemo(() => {
    const productsTotal = formData.products.reduce((acc, p) => acc + (Number(p.quantity) || 0) * (Number(p.purchase_price) || 0), 0);
    return productsTotal + (Number(formData.tax) || 0);
  }, [formData.products, formData.tax]);

  const totalPaid = useMemo(() => formData.payments.reduce((sum, p) => sum + (Number(p.amount) || 0), 0), [formData.payments]);
  const getRemainingAmount = useCallback(() => Math.max(0, grandTotal - totalPaid), [grandTotal, totalPaid]);

  const fetchInitialData = useCallback(async () => {
    try {
      const headers = { Authorization: `Bearer ${AuthToken}` };
      const [resPurchases, resProducts] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_URL}/purchases-create`, { headers }),
        axios.get(`${process.env.REACT_APP_API_URL}/products-create`, { headers }),
      ]);

      setVendors(resPurchases.data.vendors || []);
      setCategories(resProducts.data.categories || []);

      if (resPurchases.data.payment_methods) {
        setPaymentMethods(resPurchases.data.payment_methods);
      } else {
        const pmRes = await axios.get(`${process.env.REACT_APP_API_URL}/payment-methods`, { headers });
        setPaymentMethods(pmRes.data.payment_methods || []);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [AuthToken]);

  useEffect(() => {
    setSidebar(false);
    fetchInitialData();
  }, [fetchInitialData, setSidebar]);

  useEffect(() => {
    if (selectedVendor?.products) {
      setProducts(selectedVendor.products);
    } else {
      setProducts([]);
    }
  }, [selectedVendor]);

  useEffect(() => {
    if (formData.payments.length === 1 && grandTotal > 0 && formData.payments[0]?.amount !== grandTotal) {
      setFormData((prev) => ({
        ...prev,
        payments: [{ ...prev.payments[0], amount: grandTotal, payment_date: prev.payments[0].payment_date || getTodayDate() }],
      }));
    }
  }, [grandTotal]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "purchase_date") {
      const newPurchaseDate = value || getTodayDate();
      setFormData((prev) => ({
        ...prev,
        purchase_date: newPurchaseDate,
        payments: prev.payments.map((p) => ({ ...p, payment_date: "" })),
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleProductChange = (index, e) => {
    const { name, value } = e.target;
    const isNumeric = name === "quantity" || name === "extra_quantity" || name.includes("price");
    const updated = [...formData.products];
    updated[index][name] = value === "" ? "" : isNumeric ? Number(value) : value;
    setFormData((prev) => ({ ...prev, products: updated }));
  };

  const updatePreviousPrice = async (index, fieldName, productId) => {
    if (!productId) return;
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/products/${productId}/last-prices`, {
        headers: { Authorization: `Bearer ${AuthToken}` },
      });
      const updated = [...formData.products];
      updated[index][fieldName] = res.data[fieldName] || 0;
      setFormData((prev) => ({ ...prev, products: updated }));
      setForceUpdate((prev) => prev + 1);
    } catch (err) {
      console.error(err);
    }
  };

  const handlePaymentChange = (index, e) => {
    const { name, value } = e.target;
    let newValue = value;
    if (name === "amount") {
      newValue = value === "" ? "" : Number(value);
      if (newValue !== "" && (isNaN(newValue) || newValue < 0)) newValue = 0;
    }
    const updated = [...formData.payments];
    updated[index] = { ...updated[index], [name]: newValue };
    setFormData((prev) => ({ ...prev, payments: updated }));
  };

  const addProductRow = () => setFormData((prev) => ({ ...prev, products: [...prev.products, { ...emptyProductRow }] }));
  const removeProductRow = (index) => setFormData((prev) => ({ ...prev, products: prev.products.filter((_, i) => i !== index) }));

  const addPaymentRow = () => {
    const remaining = getRemainingAmount();
    if (remaining <= 0) {
      Swal.fire({ icon: "info", title: t("Info"), text: t("Purchase is already fully paid"), confirmButtonText: t("OK") });
      return;
    }
    setFormData((prev) => ({
      ...prev,
      payments: [...prev.payments, { payment_method_id: "", amount: remaining, payment_date: getTodayDate() }],
    }));
  };

  const removePaymentRow = (index) => {
    if (formData.payments.length === 1) {
      showToast("warning", t("At least one payment row is required"));
      return;
    }
    setFormData((prev) => ({ ...prev, payments: prev.payments.filter((_, i) => i !== index) }));
  };

  const openProductModal = (rowIndex = null) => {
    setCurrentProductRowIndex(rowIndex);
    setProductForm({ name: "", cat_id: "", vendor_id: formData.vendor_id || "" });
    setProductErrors({});
    setShowProductModal(true);
  };

  const handleProductSubmit = async (e) => {
    e.preventDefault();
    setProductLoading(true);
    setProductErrors({});

    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/products`, productForm, {
        headers: { Authorization: `Bearer ${AuthToken}`, "Content-Type": "application/json" },
      });

      const newProduct = response.data.product || response.data.data;
      if (newProduct?.id) {
        const vendorsRes = await axios.get(`${process.env.REACT_APP_API_URL}/purchases-create`, {
          headers: { Authorization: `Bearer ${AuthToken}` },
        });

        setVendors(vendorsRes.data.vendors);
        const currentVendor = vendorsRes.data.vendors.find((v) => v.id == formData.vendor_id);

        if (currentVendor?.products) {
          setProducts([...currentVendor.products]);
          setForceUpdate((prev) => prev + 1);

          setFormData((prev) => {
            const updated = [...prev.products];
            const targetIdx = currentProductRowIndex !== null ? currentProductRowIndex : updated.findIndex((p) => !p.product_id);
            if (targetIdx !== -1 && updated[targetIdx]) {
              updated[targetIdx] = { ...updated[targetIdx], product_id: String(newProduct.id) };
            }
            return { ...prev, products: updated };
          });

          setTimeout(() => setForceUpdate((prev) => prev + 1), 50);
        }

        setShowProductModal(false);
        setCurrentProductRowIndex(null);
        showToast("success", t("Product added successfully!"));
      }
    } catch (error) {
      if (error.response?.status === 422) {
        setProductErrors(error.response.data.errors || {});
        showToast("error", t("Please check the form for errors"));
      } else {
        showToast("error", t("Failed to add product"));
      }
    } finally {
      setProductLoading(false);
    }
  };

  const openVendorModal = () => {
    setVendorForm({ name: "", email: "", phone: "", address: "", img: null });
    setVendorErrors({});
    setShowVendorModal(true);
  };

  const handleVendorSubmit = async (e) => {
    e.preventDefault();
    setVendorLoading(true);
    setVendorErrors({});

    try {
      const payload = new FormData();
      Object.entries(vendorForm).forEach(([k, v]) => v && payload.append(k, v));

      const response = await axios.post(`${process.env.REACT_APP_API_URL}/vendors-store`, payload, {
        headers: { Authorization: `Bearer ${AuthToken}`, "Content-Type": "multipart/form-data" },
      });

      if (response.data.success) {
        const vendorsRes = await axios.get(`${process.env.REACT_APP_API_URL}/purchases-create`, {
          headers: { Authorization: `Bearer ${AuthToken}` },
        });
        setVendors(vendorsRes.data.vendors);

        const newVendor = response.data.vendor || response.data.data;
        if (newVendor?.id) setFormData((prev) => ({ ...prev, vendor_id: newVendor.id }));

        setShowVendorModal(false);
        showToast("success", t("Vendor added successfully!"));
      }
    } catch (error) {
      if (error.response?.status === 422) {
        setVendorErrors(error.response.data.errors || {});
        showToast("error", t("Please check the form for errors"));
      } else {
        showToast("error", t("Failed to add vendor"));
      }
    } finally {
      setVendorLoading(false);
    }
  };

  const handleSubmit = () => {
    setErrors({});

    if (!formData.vendor_id) {
      showToast("error", t("Please select a vendor"));
      return;
    }

    const hasValidProduct = formData.products.some((prod) => prod.product_id && prod.quantity !== "" && prod.purchase_price !== "");
    if (!hasValidProduct) {
      showToast("error", t("Please add at least one valid product with quantity and purchase price"));
      return;
    }

    const submitData = {
      ...formData,
      products: formData.products.map((p) => ({
        ...p,
        quantity: Number(p.quantity) || 0,
        extra_quantity: Number(p.extra_quantity) || 0,
        purchase_price: Number(p.purchase_price) || 0,
        sale_price: Number(p.sale_price) || 0,
        online_price: Number(p.online_price) || 0,
      })),
      payments: formData.payments.map((p) => ({ ...p, amount: Number(p.amount) || 0 })),
      tax: Number(formData.tax) || 0,
    };

    if (submitData.payments.some((p) => p.amount > 0 && !p.payment_method_id)) {
      showToast("warning", t("Please select payment method for all payments with amount greater than 0"));
      return;
    }

    if (submitData.payments.some((p) => p.amount > 0 && !p.payment_date)) {
      showToast("warning", t("Please select payment date for all payments with amount greater than 0"));
      return;
    }

    axios
      .post(`${process.env.REACT_APP_API_URL}/purchases`, submitData, {
        headers: { Authorization: `Bearer ${AuthToken}` },
      })
      .then(() => {
        showToast("success", t("Purchase created successfully!"));
        navigate("/purchase-list");
      })
      .catch((err) => {
        if (err.response?.status === 422) setErrors(err.response.data.errors);
      });
  };

  const vendorOptions = useMemo(
    () => vendors.map((v) => ({ value: v.id, label: v.company_name || v.name })),
    [vendors]
  );

  const productOptions = useMemo(
    () => products.map((p) => ({ value: p.id, label: p.name })),
    [products]
  );

  if (loading) return <PageLayout>{t("Loading...")}</PageLayout>;

  return (
    <PageLayout>
      <Row>
        <Col xl={12}>
          <Card className="mb-4 p-4">
            <Card.Body>
              <ButtonComponent className="btn btn-secondary mb-4" onClick={() => navigate("/purchase-list")} text={t("Back to Purchases")} />

              {/* Form Header Controls */}
              <Row className="mb-4 align-items-start">
                {/* Purchase Date */}
                <Col xs={12} md={4}>
                  <Form.Group>
                    <Form.Label className="fw-bold text-uppercase small mb-1">
                      {t("Purchase Date")}
                    </Form.Label>
                    <Form.Control
                      type="date"
                      name="purchase_date"
                      value={formData.purchase_date}
                      onChange={handleChange}
                      isInvalid={!!errors.purchase_date}
                      className="w-100"
                    />
                    <Form.Control.Feedback type="invalid">{errors.purchase_date}</Form.Control.Feedback>
                  </Form.Group>
                </Col>

                {/* Vendor Field */}
                <Col xs={12} md={4}>
                  <Form.Group>
                    <Form.Label className="fw-bold text-uppercase small mb-1">
                      {t("Vendor")} <span className="text-danger">*</span>
                    </Form.Label>
                    <div className="d-flex gap-2 align-items-center">
                      <div className="flex-grow-1">
                        <Select
                          options={vendorOptions}
                          value={vendorOptions.find((o) => o.value === formData.vendor_id) || null}
                          onChange={(selected) => {
                            const newVendorId = selected ? selected.value : "";
                            const currentVendor = vendors.find((v) => v.id == newVendorId);
                            const vendorProductIds = currentVendor ? currentVendor.products.map((p) => p.id) : [];

                            setFormData((prev) => ({
                              ...prev,
                              vendor_id: newVendorId,
                              products: prev.products.map((prod) => (vendorProductIds.includes(prod.product_id) ? prod : { ...prod, product_id: "" })),
                            }));
                          }}
                          isClearable
                          placeholder={t("Select Vendor")}
                          className="w-100"
                        />
                      </div>
                      <ButtonComponent
                        className="btn btn-primary d-flex align-items-center justify-content-center"
                        onClick={openVendorModal}
                        text={<FaPlus />}
                      />
                    </div>
                    {errors.vendor_id && <span className="text-danger small">{errors.vendor_id}</span>}
                  </Form.Group>
                </Col>

                {/* Upload Image Field */}
                <Col xs={12} md={4}>
                  <Form.Group>
                    <Form.Label className="fw-bold text-uppercase small mb-1">
                      {t("Upload Image")}
                    </Form.Label>
                    <Form.Control
                      type="file"
                      name="img"
                      onChange={(e) => setFormData((prev) => ({ ...prev, img: e.target.files[0] }))}
                      isInvalid={!!errors.img}
                      className="w-100"
                    />
                    <Form.Control.Feedback type="invalid">{errors.img}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>

              {/* Only show balance summary cards if a vendor is selected AND vendor balance is not 0 */}
              {formData.vendor_id && selectedVendorBalance !== 0 && (
                <Row className="mb-4">
                  <Col xs={12} md={4}>
                    <div className="p-3 bg-light rounded border">
                      <small className="text-muted">{t("Previous Balance")}</small>
                      <div>
                        <strong className={`fs-5 ${selectedVendorBalance >= 0 ? "text-success" : "text-danger"}`}>
                          {selectedVendorBalance}
                        </strong>
                      </div>
                      <small className="text-muted">{selectedVendorName}</small>
                    </div>
                  </Col>
                  <Col xs={12} md={4}>
                    <div className="p-3 bg-primary text-white rounded">
                      <small className="opacity-75">{t("Current Purchase")}</small>
                      <div><strong className="fs-5">{grandTotal}</strong></div>
                      <small className="opacity-75">{t("Including Tax")}</small>
                    </div>
                  </Col>
                  <Col xs={12} md={4}>
                    <div className="p-3 bg-warning text-dark rounded">
                      <small className="opacity-75">{t("Total Balance After Purchase")}</small>
                      <div><strong className="fs-5">{selectedVendorBalance + grandTotal}</strong></div>
                      <small className="opacity-75">{t("Previous + Current Purchase")}</small>
                    </div>
                  </Col>
                </Row>
              )}

              <h4 className="mb-3">{t("Products")} <span className="text-danger">*</span></h4>
              {formData.products.map((prod, index) => {
                const selectedProduct = products.find(
                  (product) => String(product.id) === String(prod.product_id)
                );
                const isOnline = selectedProduct?.is_online === 1;
                const availableStock = Number(selectedProduct?.available_stock) || 0;
                const purchaseQuantity = Number(prod.quantity) || 0;
                const giftQuantity = Number(prod.extra_quantity) || 0;

                return (
                  <Card key={index} className="mb-3 p-3 bg-light border rounded">
                    <Row className="align-items-stretch">
                      <Col xl={1} className="d-flex flex-column">
                        <ButtonComponent className="btn btn-danger btn-sm mt-4" onClick={() => removeProductRow(index)} text="X" />
                      </Col>

                      <Col xl={5} className="d-flex flex-column">
                        <label className="fw-bold mb-1">{t("Product")} <span className="text-danger">*</span></label>
                        <div className="d-flex gap-2 align-items-center">
                          <div className="flex-grow-1">
                            <Select
                              key={`product-select-${forceUpdate}-${index}`}
                              options={productOptions}
                              value={productOptions.find((p) => p.value == prod.product_id) || null}
                              onChange={async (selected) => {
                                const updated = [...formData.products];
                                updated[index].product_id = selected ? selected.value : "";
                                setFormData((prev) => ({ ...prev, products: updated }));
                              }}
                              isClearable
                              placeholder={t("Select Product")}
                            />
                          </div>
                          <ButtonComponent
                            className="btn btn-primary d-flex align-items-center justify-content-center"
                            onClick={() => openProductModal(index)}
                            text={<FaPlus />}
                          />
                        </div>
                        {selectedProduct && (
                          <small className="text-muted mt-1">
                            {t("Available Stock")}: {availableStock} + {purchaseQuantity} + {giftQuantity} ={" "}
                            {availableStock + purchaseQuantity + giftQuantity}
                          </small>
                        )}
                      </Col>

                      <Col xl={1} className="d-flex flex-column">
                        <label className="fw-bold mb-1">{t("Qty")} <span className="text-danger">*</span></label>
                        <input
                          type="number"
                          name="quantity"
                          value={prod.quantity}
                          min={0}
                          placeholder={t("Qty")}
                          onChange={(e) => handleProductChange(index, e)}
                          className="form-control"
                        />
                      </Col>

                      <Col xl={1} className="d-flex flex-column">
                        <label className="fw-bold mb-1">{t("Gift Qty")}</label>
                        <input
                          type="number"
                          name="extra_quantity"
                          value={prod.extra_quantity}
                          min={0}
                          placeholder={t("Gift Qty")}
                          onChange={(e) => handleProductChange(index, e)}
                          className="form-control"
                        />
                      </Col>

                      <Col xl={1} className="d-flex flex-column">
                        <label className="fw-bold mb-1">{t("Purchase")} <span className="text-danger">*</span></label>
                        <input
                          type="number"
                          name="purchase_price"
                          value={prod.purchase_price}
                          min={0}
                          step="0.01"
                          placeholder={t("Price")}
                          onChange={(e) => handleProductChange(index, e)}
                          className="form-control"
                        />
                        <label className="mt-1 small user-select-none role-button">
                          <input
                            type="checkbox"
                            className="me-1"
                            onChange={(e) => e.target.checked && updatePreviousPrice(index, "purchase_price", prod.product_id)}
                          />
                          {t("Prev")}
                        </label>
                      </Col>

                      <Col xl={1} className="d-flex flex-column">
                        <label className="fw-bold mb-1">{t("Sale")} <span className="text-danger">*</span></label>
                        <input
                          type="number"
                          name="sale_price"
                          value={prod.sale_price}
                          min={0}
                          step="0.01"
                          placeholder={t("Sale Price")}
                          onChange={(e) => handleProductChange(index, e)}
                          className="form-control"
                        />
                        <label className="mt-1 small user-select-none role-button">
                          <input
                            type="checkbox"
                            className="me-1"
                            onChange={(e) => e.target.checked && updatePreviousPrice(index, "sale_price", prod.product_id)}
                          />
                          {t("Prev")}
                        </label>
                      </Col>

                      <Col xl={1} className="d-flex flex-column">
                        <label className="fw-bold mb-1">{t("Total")}</label>
                        <input
                          type="number"
                          value={(Number(prod.quantity) || 0) * (Number(prod.purchase_price) || 0)}
                          readOnly
                          className="form-control bg-body-secondary"
                        />
                      </Col>

                      {isOnline && (
                        <Col xl={1} className="d-flex flex-column">
                          <label className="fw-bold mb-1">{t("Online Price")}</label>
                          <input
                            type="number"
                            name="online_price"
                            min={0}
                            step="0.01"
                            value={prod.online_price}
                            placeholder={t("Online")}
                            onChange={(e) => handleProductChange(index, e)}
                            className="form-control"
                          />
                          <label className="mt-1 small user-select-none role-button">
                            <input
                              type="checkbox"
                              className="me-1"
                              onChange={(e) => e.target.checked && updatePreviousPrice(index, "online_price", prod.product_id)}
                            />
                            {t("Prev")}
                          </label>
                        </Col>
                      )}
                    </Row>
                  </Card>
                );
              })}

              <Row className="mb-4 align-items-start">
                <Col>
                  <ButtonComponent className="btn btn-primary" onClick={addProductRow} text={t("Add Product")} />
                </Col>

                <Col md="auto">
                  <div className="p-2 min-w-250">
                    <div className="d-flex justify-content-between mb-2">
                      <strong>{t("Subtotal")}</strong>
                      <span>{grandTotal - (Number(formData.tax) || 0)}</span>
                    </div>

                    <div className="d-flex align-items-center justify-content-between mb-2 gap-2">
                      <label className="mb-0 fw-bold small">{t("Tax Amount")}</label>
                      <input
                        type="number"
                        name="tax"
                        value={formData.tax}
                        onChange={handleChange}
                        placeholder="0"
                        min="0"
                        step="0.01"
                        className="form-control form-control-sm w-auto text-end"
                      />
                    </div>

                    <div className="d-flex justify-content-between pt-2 fw-bold fs-5 border-top">
                      <span>{t("Grand Total")}</span>
                      <span>{grandTotal}</span>
                    </div>
                  </div>
                </Col>
              </Row>

              <Row className="mb-4">
                <Col xl={6}>
                  <LabelTextareaComponent label={t("Notes")} name="notes" value={formData.notes} onChange={handleChange} />
                </Col>
                <Col xl={6}>
                  <h5 className="mb-3">{t("Payments")}</h5>
                  {formData.payments.map((pay, index) => {
                    // Extract payment methods selected in other rows
                    const selectedMethodsInOtherRows = formData.payments
                      .filter((_, i) => i !== index)
                      .map((p) => String(p.payment_method_id))
                      .filter(Boolean);

                    // Filter out selected payment methods for this dropdown
                    const availablePaymentMethods = paymentMethods.filter(
                      (m) => !selectedMethodsInOtherRows.includes(String(m.id)) || String(m.id) === String(pay.payment_method_id)
                    );

                    return (
                      <Card key={index} className="mb-3 p-3 bg-light border rounded">
                        <Row className="align-items-center">
                          <Col xs={4}>
                            <label className="fw-bold mb-1">{t("Payment Method")}</label>
                            <select
                              name="payment_method_id"
                              value={pay.payment_method_id}
                              onChange={(e) => handlePaymentChange(index, e)}
                              className="form-select"
                            >
                              <option value="">{t("Select Method")}</option>
                              {availablePaymentMethods.map((m) => (
                                <option key={m.id} value={m.id}>{m.name}</option>
                              ))}
                            </select>
                          </Col>
                          <Col xs={3}>
                            <label className="fw-bold mb-1">{t("Amount")}</label>
                            <input
                              type="number"
                              name="amount"
                              value={pay.amount}
                              placeholder={t("Enter amount")}
                              onChange={(e) => handlePaymentChange(index, e)}
                              className="form-control"
                            />
                          </Col>
                          <Col xs={4}>
                            <label className="fw-bold mb-1">{t("Payment Date")} <span className="text-danger">*</span></label>
                            <input
                              type="date"
                              name="payment_date"
                              value={pay.payment_date || ""}
                              onChange={(e) => handlePaymentChange(index, e)}
                              className="form-control"
                            />
                          </Col>
                          <Col xs={1} className="text-center">
                            <ButtonComponent
                              className="btn btn-danger btn-sm mt-4"
                              onClick={() => removePaymentRow(index)}
                              text="X"
                              disabled={formData.payments.length === 1}
                            />
                          </Col>
                        </Row>
                      </Card>
                    );
                  })}
                  <ButtonComponent
                    className="btn btn-primary btn-sm mt-1"
                    onClick={addPaymentRow}
                    text={t("Add Payment")}
                    disabled={getRemainingAmount() <= 0}
                  />
                </Col>
              </Row>

              <ButtonComponent className="mc-btn primary w-100 mt-3" onClick={handleSubmit} text={t("Create Purchase")} />
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Vendor Modal */}
      <Modal show={showVendorModal} onHide={() => setShowVendorModal(false)} size="lg" centered>
        <Modal.Header closeButton className="p-2">
          <Modal.Title>{t("Add New Vendor")}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleVendorSubmit}>
          <Modal.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>{t("name")} <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={vendorForm.name}
                    onChange={(e) => setVendorForm((p) => ({ ...p, name: e.target.value }))}
                    isInvalid={!!vendorErrors.name}
                    placeholder={t("Enter vendor name")}
                  />
                  <Form.Control.Feedback type="invalid">{vendorErrors.name}</Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>{t("email")}</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={vendorForm.email}
                    onChange={(e) => setVendorForm((p) => ({ ...p, email: e.target.value }))}
                    isInvalid={!!vendorErrors.email}
                    placeholder={t("Enter email address")}
                  />
                  <Form.Control.Feedback type="invalid">{vendorErrors.email}</Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>{t("phone")}</Form.Label>
                  <Form.Control
                    type="text"
                    name="phone"
                    value={vendorForm.phone}
                    onChange={(e) => setVendorForm((p) => ({ ...p, phone: e.target.value }))}
                    isInvalid={!!vendorErrors.phone}
                    placeholder={t("Enter phone number")}
                  />
                  <Form.Control.Feedback type="invalid">{vendorErrors.phone}</Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>{t("image")}</Form.Label>
                  <Form.Control
                    type="file"
                    name="img"
                    onChange={(e) => setVendorForm((p) => ({ ...p, img: e.target.files[0] }))}
                    accept="image/*"
                    isInvalid={!!vendorErrors.img}
                  />
                  <Form.Control.Feedback type="invalid">{vendorErrors.img}</Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>{t("address")}</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                name="address"
                value={vendorForm.address}
                onChange={(e) => setVendorForm((p) => ({ ...p, address: e.target.value }))}
                isInvalid={!!vendorErrors.address}
                placeholder={t("Enter address")}
              />
              <Form.Control.Feedback type="invalid">{vendorErrors.address}</Form.Control.Feedback>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer className="p-2">
            <ButtonComponent className="btn btn-secondary" onClick={() => setShowVendorModal(false)} text={t("cancel")} />
            <ButtonComponent className="btn btn-primary" type="submit" disabled={vendorLoading}>
              {vendorLoading ? <><Spinner animation="border" size="sm" className="me-2" />{t("saving")}...</> : t("save")}
            </ButtonComponent>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Product Modal */}
      <Modal show={showProductModal} onHide={() => setShowProductModal(false)} size="lg" centered>
        <Modal.Header closeButton className="p-2">
          <Modal.Title>{t("Add New Product")}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleProductSubmit}>
          <Modal.Body>
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>{t("Product Name")} <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={productForm.name}
                    onChange={(e) => setProductForm((p) => ({ ...p, name: e.target.value }))}
                    isInvalid={!!productErrors.name}
                    placeholder={t("Enter product name")}
                  />
                  <Form.Control.Feedback type="invalid">{productErrors.name}</Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>{t("Category")} <span className="text-danger">*</span></Form.Label>
                  <Select
                    options={categories.map((c) => ({ value: c.id, label: c.name }))}
                    value={categories.map((c) => ({ value: c.id, label: c.name })).find((o) => o.value == productForm.cat_id) || null}
                    onChange={(opt) => setProductForm((p) => ({ ...p, cat_id: opt ? opt.value : "" }))}
                    placeholder={t("Select Category")}
                    isClearable
                  />
                  {productErrors.cat_id && <div className="invalid-feedback d-block">{productErrors.cat_id}</div>}
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>{t("Vendor")} <span className="text-danger">*</span></Form.Label>
                  <Select
                    options={vendorOptions}
                    value={vendorOptions.find((o) => o.value == productForm.vendor_id) || null}
                    placeholder={t("Select Vendor")}
                    isDisabled
                  />
                  {productErrors.vendor_id && <div className="invalid-feedback d-block">{productErrors.vendor_id}</div>}
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer className="p-2">
            <ButtonComponent className="btn btn-secondary" onClick={() => setShowProductModal(false)} text={t("cancel")} />
            <ButtonComponent className="btn btn-primary" type="submit" disabled={productLoading}>
              {productLoading ? <><Spinner animation="border" size="sm" className="me-2" />{t("saving")}...</> : t("save")}
            </ButtonComponent>
          </Modal.Footer>
        </Form>
      </Modal>
    </PageLayout>
  );
}